import { createBrowserRouter } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { ProtectedRoute } from '@/components/guards/ProtectedRoute'
import { InstallGuard } from '@/components/guards/InstallGuard'
import { OptionalInstallPage, installFrontendEnabled } from '@/install-bridge'
import { PublicLayout, UserLayout, AdminLayout } from '@/components/layouts'
import RouteErrorPage from '@/pages/RouteErrorPage'

// 加载占位组件
function PageLoader() {
  return (
    <div className="flex min-h-[calc(100vh-64px-76px)] items-center justify-center">
      <div className="text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
        <p className="mt-2 text-sm text-slate-500">加载中...</p>
      </div>
    </div>
  )
}

// ========== 主页区域（公开） ==========
const HomePage = lazy(() => import('@/pages/home/HomePage'))
const SkinLibraryPage = lazy(() => import('@/pages/home/SkinLibraryPage'))
const BlueprintLibraryPage = lazy(() => import('@/pages/home/BlueprintLibraryPage'))
const BlueprintPreviewPage = lazy(() => import('@/pages/blueprints/BlueprintPreviewPage'))
const ServerStatusPage = lazy(() => import('@/pages/home/ServerStatusPage'))
const AnnouncementsPage = lazy(() => import('@/pages/home/AnnouncementsPage'))
const AnnouncementDetailPage = lazy(
  () => import('@/pages/home/AnnouncementDetailPage')
)

// ========== 认证页面（公开） ==========
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const RegisterGamePage = lazy(() => import('@/pages/auth/RegisterGamePage'))
const OfficialAccountCallbackPage = lazy(
  () => import('@/pages/auth/OfficialAccountCallbackPage')
)
const QQLoginCallbackPage = lazy(() => import('@/pages/auth/QQLoginCallbackPage'))

// ========== 用户仪表板（需登录） ==========
const UserDashboardPage = lazy(() => import('@/pages/user/UserDashboardPage'))
const OfficialAccountsPage = lazy(() => import('@/pages/user/OfficialAccountsPage'))
const UserSkinsPage = lazy(() => import('@/pages/user/UserSkinsPage'))
const UploadSkinPage = lazy(() => import('@/pages/user/UploadSkinPage'))
const UserBlueprintsPage = lazy(() => import('@/pages/user/UserBlueprintsPage'))
const UploadBlueprintPage = lazy(() => import('@/pages/user/UploadBlueprintPage'))
const UploadBlueprintPreviewPage = lazy(() => import('@/pages/user/UploadBlueprintPreviewPage'))
const CheckInPage = lazy(() => import('@/pages/user/CheckInPage'))
const UserActivitiesPage = lazy(() => import('@/pages/user/UserActivitiesPage'))
const GameAccountsPage = lazy(() => import('@/pages/user/GameAccountsPage'))
const GameAccountManagePage = lazy(() => import('@/pages/user/GameAccountManagePage'))
const UserRechargeRecordsPage = lazy(() => import('@/pages/user/UserRechargeRecordsPage'))
const UserNotificationsPage = lazy(() => import('@/pages/user/UserNotificationsPage'))
const UserSettingsPage = lazy(() => import('@/pages/user/UserSettingsPage'))

// ========== 管理仪表板（需管理员） ==========
const AdminDashboardPage = lazy(
  () => import('@/pages/admin/AdminDashboardPage')
)
const AdminServersPage = lazy(() => import('@/pages/admin/AdminServersPage'))
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'))
const AdminGameAccountsPage = lazy(
  () => import('@/pages/admin/AdminGameAccountsPage')
)
const AdminSkinsPage = lazy(() => import('@/pages/admin/AdminSkinsPage'))
const AdminBlueprintsPage = lazy(() => import('@/pages/admin/AdminBlueprintsPage'))
const AdminRechargePage = lazy(() => import('@/pages/admin/AdminRechargePage'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'))
const AdminAnnouncementsPage = lazy(
  () => import('@/pages/admin/AdminAnnouncementsPage')
)
const AdminActivitiesPage = lazy(() => import('@/pages/admin/AdminActivitiesPage'))
const AdminActivitySubmissionsPage = lazy(() => import('@/pages/admin/AdminActivitySubmissionsPage'))
const AdminRewardPackagesPage = lazy(() => import('@/pages/admin/AdminRewardPackagesPage'))
const AdminCustomRewardItemsPage = lazy(() => import('@/pages/admin/AdminCustomRewardItemsPage'))
const AdminCheckInRewardRulesPage = lazy(() => import('@/pages/admin/AdminCheckInRewardRulesPage'))
const QQBotPage = lazy(() => import('@/pages/admin/QQBotPage'))

// 包装组件，添加 Suspense
function withSuspense(Component: React.ComponentType) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  )
}

/**
 * 路由配置
 * 三大区域：
 * 1. 主页区域（公开）- /
 * 2. 用户仪表板（需登录）- /user/*
 * 3. 管理仪表板（需管理员）- /admin/*
 */
export const router = createBrowserRouter([
  // ========== 主页区域（无需鉴权） ==========
  {
    path: '/',
    element: (
      <InstallGuard>
        <PublicLayout />
      </InstallGuard>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: withSuspense(HomePage) },
      { path: 'skins', element: withSuspense(SkinLibraryPage) },
      { path: 'blueprints', element: withSuspense(BlueprintLibraryPage) },
      { path: 'blueprints/:blueprintId/preview', element: withSuspense(BlueprintPreviewPage) },
      { path: 'servers', element: withSuspense(ServerStatusPage) },
      { path: 'announcements', element: withSuspense(AnnouncementsPage) },
      { path: 'announcements/:announcementId', element: withSuspense(AnnouncementDetailPage) },
    ],
  },

  // ========== 认证页面（无需鉴权） ==========
  {
    path: '/auth',
    element: (
      <InstallGuard>
        <PublicLayout />
      </InstallGuard>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { path: 'login', element: withSuspense(LoginPage) },
      { path: 'register', element: withSuspense(RegisterPage) },
      { path: 'forgot-password', element: withSuspense(ForgotPasswordPage) },
      { path: 'register-game', element: withSuspense(RegisterGamePage) },
      { path: 'official-account-callback', element: withSuspense(OfficialAccountCallbackPage) },
      { path: 'login/qq/callback', element: withSuspense(QQLoginCallbackPage) },
    ],
  },

  ...(installFrontendEnabled && OptionalInstallPage
    ? [{
        path: '/install',
        element: (
          <InstallGuard allowInstallPage>
            {withSuspense(OptionalInstallPage)}
          </InstallGuard>
        ),
        errorElement: <RouteErrorPage />,
      }]
    : []),

  // ========== 用户仪表板（需登录） ==========
  {
    path: '/user',
    element: (
      <InstallGuard>
        <ProtectedRoute>
          <PublicLayout>
            <UserLayout />
          </PublicLayout>
        </ProtectedRoute>
      </InstallGuard>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: withSuspense(UserDashboardPage) },
      { path: 'official-accounts', element: withSuspense(OfficialAccountsPage) },
      { path: 'skins', element: withSuspense(UserSkinsPage) },
      { path: 'skins/upload', element: withSuspense(UploadSkinPage) },
      { path: 'blueprints', element: withSuspense(UserBlueprintsPage) },
      { path: 'blueprints/upload', element: withSuspense(UploadBlueprintPage) },
      { path: 'blueprints/upload/preview', element: withSuspense(UploadBlueprintPreviewPage) },
      { path: 'blueprints/:blueprintId/preview', element: withSuspense(BlueprintPreviewPage) },
      { path: 'checkin', element: withSuspense(CheckInPage) },
      { path: 'activities', element: withSuspense(UserActivitiesPage) },
      { path: 'game-accounts', element: withSuspense(GameAccountsPage) },
      { path: 'game-accounts/:accountId', element: withSuspense(GameAccountManagePage) },
      { path: 'recharge-records', element: withSuspense(UserRechargeRecordsPage) },
      { path: 'notifications', element: withSuspense(UserNotificationsPage) },
      { path: 'settings', element: withSuspense(UserSettingsPage) },
    ],
  },

  // ========== 管理仪表板（需管理员权限） ==========
  {
    path: '/admin',
    element: (
      <InstallGuard>
        <ProtectedRoute requiredRole="admin">
          <PublicLayout>
            <AdminLayout />
          </PublicLayout>
        </ProtectedRoute>
      </InstallGuard>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: withSuspense(AdminDashboardPage) },
      { path: 'servers', element: withSuspense(AdminServersPage) },
      { path: 'users', element: withSuspense(AdminUsersPage) },
      { path: 'game-accounts', element: withSuspense(AdminGameAccountsPage) },
      { path: 'skins', element: withSuspense(AdminSkinsPage) },
      { path: 'blueprints', element: withSuspense(AdminBlueprintsPage) },
      { path: 'blueprints/:blueprintId/preview', element: withSuspense(BlueprintPreviewPage) },
      { path: 'recharge', element: withSuspense(AdminRechargePage) },
      { path: 'settings', element: withSuspense(AdminSettingsPage) },
      { path: 'bots', element: withSuspense(QQBotPage) },
      { path: 'announcements', element: withSuspense(AdminAnnouncementsPage) },
      { path: 'activities', element: withSuspense(AdminActivitiesPage) },
      { path: 'activities/:activityId/submissions', element: withSuspense(AdminActivitySubmissionsPage) },
      { path: 'reward-packages', element: withSuspense(AdminRewardPackagesPage) },
      { path: 'custom-reward-items', element: withSuspense(AdminCustomRewardItemsPage) },
      { path: 'checkin-reward-rules', element: withSuspense(AdminCheckInRewardRulesPage) },
    ],
  },

  // ========== 404 页面 ==========
  {
    path: '*',
    element: (
      <InstallGuard>
        <PublicLayout>
          <div className="flex min-h-[calc(100vh-64px-76px)] flex-col items-center justify-center">
            <h1 className="text-6xl font-bold text-slate-900 dark:text-slate-100">
              404
            </h1>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
              页面未找到
            </p>
            <a
              href="/"
              className="mt-6 rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700"
            >
              返回首页
            </a>
          </div>
        </PublicLayout>
      </InstallGuard>
    ),
  },
])
